import { Fragment } from 'preact';
import {
  ACCOUNT_KINDS,
  accountRows,
  bankTotal,
  cashInHand,
  type AccountKind,
  type TransferEntry,
} from '@khata/core';
import {
  Empty,
  Kpi,
  MonthPicker,
  PageHead,
  Row,
  ShowMore,
  VoidedToggle,
} from '../components/index.js';
import { taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { entries, master, state, today } from '../lib/khata.js';
import { accountKind, describe } from '../lib/labels.js';
import { listEntries, monthsWithEntries, periodRange } from '../lib/lists.js';
import { usePaged } from '../lib/paging.js';
import { listPeriod, openSheet, showVoided } from '../lib/ui.js';
import { go } from '../lib/router.js';

const GROUP: Record<AccountKind, () => string> = {
  cash: () => t('bank_groupCash'),
  bank: () => t('bank_groupBank'),
  mobile: () => t('bank_groupMobile'),
};

export function Bank() {
  const s = state.value;
  const m = master.value;
  const rows = accountRows(s, m);
  const groups = ACCOUNT_KINDS.map((kind) => ({
    kind,
    rows: rows.filter((r) => r.account.kind === kind),
  })).filter((g) => g.rows.length > 0);
  const groupTotal = (g: (typeof groups)[number]) => g.rows.reduce((tt, r) => tt + r.balance, 0);
  const moves = listEntries(
    s,
    entries.value,
    (e): e is TransferEntry => e.type === 'transfer',
    showVoided.value,
    periodRange(listPeriod.value, today.value),
  );
  const paged = usePaged(moves, 100);

  return (
    <>
      <PageHead
        title={t('bank_title')}
        sub={t('bank_sub', { cash: taka(cashInHand(s, m)), bank: taka(bankTotal(s, m)) })}
      >
        <button
          type="button"
          class="btn btn-primary"
          onClick={() => openSheet({ kind: 'transfer' })}
        >
          {t('bank_transfer')}
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          onClick={() => openSheet({ kind: 'account' })}
        >
          {t('bank_account')}
        </button>
      </PageHead>
      {groups.map((g) => (
        <Fragment key={g.kind}>
          {groups.length > 1 && (
            <h6 class="section" data-testid={'group-' + g.kind}>
              {GROUP[g.kind]()} · {taka(groupTotal(g) as never)}
            </h6>
          )}
          <section class="kpis">
            {g.rows.map((r) => (
              <button
                key={r.account.id}
                type="button"
                style={{
                  background: 'transparent',
                  border: 0,
                  padding: 0,
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
                onClick={() => go('ledger', r.account.id)}
                data-testid={'account-' + r.account.id}
              >
                <Kpi
                  label={r.account.name + (r.account.active ? '' : ` (${t('bank_closed')})`)}
                  value={taka(r.balance)}
                  sub={accountKind(r.account.kind)}
                />
              </button>
            ))}
          </section>
        </Fragment>
      ))}
      <h6 class="section">{t('bank_movements')}</h6>
      <MonthPicker months={monthsWithEntries(entries.value)} />
      <VoidedToggle />
      <div class="list">
        {moves.length === 0 && <Empty>{t('bank_empty')}</Empty>}
        {paged.visible.map(({ entry: e, voided }) => {
          const l = describe(m, e);
          return (
            <Row
              key={e.id}
              title={l.title}
              sub={l.sub}
              amount={l.amount}
              amountTone={voided ? 'muted' : l.tone}
              amountSub={voided ? t('voided') : undefined}
              strike={voided}
              onClick={() => openSheet({ kind: 'entry', id: e.id })}
            />
          );
        })}
      </div>
      <ShowMore shown={paged.shown} total={paged.total} onMore={paged.more} />
    </>
  );
}
